import { Injectable } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { PaginatedResponse } from '../../common/models/common.model';
import {
  AdvanceLedger,
  Labour,
  LabourAdvance,
  LabourAdvanceRequest,
  LabourBasicInfo,
  LabourCheckIn,
  LabourCheckInRequest,
  LabourCheckInResponse,
  LabourCheckOutRequest,
  LabourCheckOutResponse,
} from '../production-inventory.model';
import { Observable } from 'rxjs';
import { buildFilterQuery, buildHttpParams } from '../../utils';

@Injectable({
  providedIn: 'root',
})
export class LaboursService {
  private readonly baseUrl: string = 'labour/';

  constructor(private apiService: ApiService) {}

  public list(q = {}): Observable<PaginatedResponse<Labour>> {
    const params = buildHttpParams(q);
    return this.apiService.get(this.baseUrl, params);
  }

  public post(reqBody: any): Observable<Labour> {
    return this.apiService.post(this.baseUrl, reqBody);
  }

  public update(id: string, reqBody: any): Observable<Labour> {
    return this.apiService.put(this.baseUrl + `${id}/`, reqBody);
  }

  public delete(id: string): Observable<void> {
    return this.apiService.delete(`${this.baseUrl}${id}/`);
  }

  public lighter_list(q = {}): Observable<LabourBasicInfo[]> {
    const params = buildHttpParams(q);
    return this.apiService.get(this.baseUrl + 'lighter_list/', params);
  }

  public todays_attendance(q = {}): Observable<PaginatedResponse<LabourCheckIn>> {
    const params = buildHttpParams(q);
    return this.apiService.get(this.baseUrl + 'labour-attendance/checked-in-today/', params);
  }

  public check_in(reqBody: LabourCheckInRequest): Observable<LabourCheckInResponse> {
    return this.apiService.post(this.baseUrl + 'labour-attendance/check-in/', reqBody);
  }

  public check_out(reqBody: LabourCheckOutRequest): Observable<LabourCheckOutResponse> {
    return this.apiService.post(this.baseUrl + 'labour-attendance/check-out/', reqBody);
  }

  public add_advance(id: string, reqBody: LabourAdvanceRequest): Observable<LabourAdvance> {
    const formData = new FormData();
    formData.append('amount', String(reqBody.amount));
    formData.append('date', reqBody.date);
    if (reqBody.note) formData.append('note', reqBody.note);
    return this.apiService.post(this.baseUrl + `${id}/add-advance/`, formData);
  }

  public get_advances(id: string, q = {}): Observable<LabourAdvance[]> {
    const params = buildHttpParams(q);
    return this.apiService.get(this.baseUrl + `${id}/advances/`, params);
  }

  /** Full advance history; `from_date`/`to_date` (YYYY-MM-DD) fold earlier entries into opening_balance. */
  public advance_ledger(id: string, q: { from_date?: string; to_date?: string } = {}): Observable<AdvanceLedger> {
    const params = buildHttpParams(buildFilterQuery(q));
    return this.apiService.get(this.baseUrl + `${id}/advance-ledger/`, params);
  }

  public download_summary(q = {}): Observable<any> {
    const params = buildHttpParams(q);
    return this.apiService.get(this.baseUrl + 'labour-attendance/download_summary/', params);
  }

  public get_salary_details(q = {}): Observable<any> {
    const params = buildHttpParams(q);
    return this.apiService.get(this.baseUrl + 'labour-attendance/payroll-summary/', params);
  }

  public download_salary_sheet(q = {}): Observable<{ file_url: string }> {
    const params = buildHttpParams(q);
    return this.apiService.get(this.baseUrl + 'labour-attendance/download-salary-sheet/', params);
  }

  public save_salary_payments(reqBody: any): Observable<any> {
    return this.apiService.post(this.baseUrl + 'labour-attendance/pay-salary/', reqBody);
  }

  public export_attendance(q = {}): Observable<Blob> {
    const params = buildHttpParams(q);
    return this.apiService.getBlob(this.baseUrl + 'labour-attendance/export-attendance/', params);
  }
}
